export const RESTAURANT_SERVICE_TYPES = [
  'JREATS',
  'MYEATS',
  'DINEIN',
  'CATERING',
  'PARTY_HALL',
];

export const RESTAURANT_SERVICE_LABELS = {
  JREATS: 'Jr Eats',
  MYEATS: 'My Eats',
  DINEIN: 'Dine-In',
  CATERING: 'Catering',
  PARTY_HALL: 'Party Hall',
};

export const CAPACITY_RESTAURANT_SERVICE_TYPES = new Set(['CATERING', 'PARTY_HALL']);
export const DINEIN_RESTAURANT_SERVICE_TYPES = new Set(['DINEIN']);
export const PARTY_HALL_SERVICE_TYPE = 'PARTY_HALL';

export const PARTY_HALL_TYPES = [
  { value: 'WEDDING', label: 'Wedding / Marriage' },
  { value: 'BIRTHDAY', label: 'Birthday / Party' },
  { value: 'BANQUET', label: 'Banquet Hall' },
  { value: 'FARMHOUSE', label: 'Farmhouse' },
  { value: 'CONFERENCE', label: 'Conference' },
  { value: 'OTHER', label: 'Other' },
];

export const PARTY_HALL_TYPE_LABELS = PARTY_HALL_TYPES.reduce((acc, row) => {
  acc[row.value] = row.label;
  return acc;
}, {});

/** Preset amenities for party hall setup (multi-select). */
export const PARTY_HALL_AMENITY_OPTIONS = [
  'AC',
  'Parking',
  'Stage',
  'Sound System',
  'Lighting',
  'Projector / Screen',
  'WiFi',
  'Generator',
  'Catering Kitchen',
  'Decoration Support',
  'Valet',
  'Washrooms',
  'Garden / Outdoor',
  'Changing Room',
  'Other'
];

/** Preset event types for party hall setup (multi-select). */
export const PARTY_HALL_EVENT_TYPE_OPTIONS = [
  'Wedding',
  'Reception',
  'Engagement',
  'Mehendi',
  'Sangeet',
  'Birthday',
  'Anniversary',
  'Baby Shower',
  'Corporate Event',
  'Conference',
  'Meeting',
  'Other'
];

export function splitPartyHallListValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (value == null || value === '') return [];
  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

export function joinPartyHallListValue(values) {
  if (!Array.isArray(values)) return splitPartyHallListValue(values).join(', ');
  return values.map((item) => String(item).trim()).filter(Boolean).join(', ');
}

const emptyPartyHallFields = () => ({
  hallName: '',
  hallType: '',
  description: '',
  basePrice: '',
  amenities: '',
  eventTypes: '',
});

function splitListValue(value) {
  return splitPartyHallListValue(value);
}

function joinListValue(value) {
  if (Array.isArray(value)) return joinPartyHallListValue(value);
  if (value == null) return '';
  return String(value);
}

export function extractPartyHallFields(row = {}) {
  const hall = row.partyHall || row.party_hall || row;
  const partyHallId = hall.partyHallId ?? hall.Party_Hall_Id ?? null;
  return {
    partyHallId:
      partyHallId != null && partyHallId !== '' ? Number(partyHallId) : null,
    hallName: hall.hallName ?? hall.Hall_Name ?? '',
    hallType: hall.hallType ?? hall.Hall_Type ?? '',
    description: hall.description ?? hall.Description ?? '',
    basePrice:
      hall.basePrice != null && hall.basePrice !== ''
        ? String(hall.basePrice)
        : hall.Base_Price != null && hall.Base_Price !== ''
          ? String(hall.Base_Price)
          : '',
    amenities: joinListValue(hall.amenities ?? hall.Amenities),
    eventTypes: joinListValue(hall.eventTypes ?? hall.Event_Types),
  };
}

export function buildPartyHallPayload(row = {}) {
  const hall = {
    hallName: String(row.hallName || '').trim() || undefined,
    hallType: String(row.hallType || '').trim().toUpperCase() || undefined,
    description: String(row.description || '').trim() || undefined,
    amenities: splitListValue(row.amenities),
    eventTypes: splitListValue(row.eventTypes),
  };

  if (row.basePrice !== '' && row.basePrice != null) {
    hall.basePrice = Number(row.basePrice);
  }

  return hall;
}

export function buildServicePayload(serviceType, row = {}) {
  const payload = {
    serviceType,
    status: row.status || 'ACTIVE',
  };

  if (CAPACITY_RESTAURANT_SERVICE_TYPES.has(serviceType)) {
    payload.minGuestCount = Number(row.minGuestCount);
    if (row.maxGuestCount !== '' && row.maxGuestCount != null) {
      payload.maxGuestCount = Number(row.maxGuestCount);
    }
  }

  if (DINEIN_RESTAURANT_SERVICE_TYPES.has(serviceType)) {
    if (row.noOfTables !== '' && row.noOfTables != null) {
      payload.noOfTables = Number(row.noOfTables);
    }
  }

  if (serviceType === PARTY_HALL_SERVICE_TYPE) {
    payload.partyHall = buildPartyHallPayload(row);
  }

  return payload;
}

export function validateServicePayload(serviceType, row = {}) {
  if (CAPACITY_RESTAURANT_SERVICE_TYPES.has(serviceType)) {
    const minGuestCount = Number(row.minGuestCount);
    if (!Number.isFinite(minGuestCount) || minGuestCount <= 0) {
      return `Min guest count is required and must be greater than 0 for ${RESTAURANT_SERVICE_LABELS[serviceType] || serviceType}.`;
    }

    if (row.maxGuestCount !== '' && row.maxGuestCount != null) {
      const maxGuestCount = Number(row.maxGuestCount);
      if (!Number.isFinite(maxGuestCount) || maxGuestCount < minGuestCount) {
        return 'Max guest count must be greater than or equal to min guest count.';
      }
    }
  }

  if (DINEIN_RESTAURANT_SERVICE_TYPES.has(serviceType)) {
    if (row.noOfTables === '' || row.noOfTables == null) {
      return 'Number of tables is required for Dine-In.';
    }
    const noOfTables = Number(row.noOfTables);
    if (!Number.isFinite(noOfTables) || noOfTables <= 0) {
      return 'Number of tables must be greater than 0 for Dine-In.';
    }
  }

  if (serviceType === PARTY_HALL_SERVICE_TYPE) {
    const hallType = String(row.hallType || '').trim().toUpperCase();
    if (!hallType) {
      return 'Hall type is required for Party Hall.';
    }
    if (!PARTY_HALL_TYPE_LABELS[hallType]) {
      return 'Select a valid hall type.';
    }
    if (row.basePrice !== '' && row.basePrice != null) {
      const basePrice = Number(row.basePrice);
      if (!Number.isFinite(basePrice) || basePrice < 0) {
        return 'Base price must be a number greater than or equal to 0.';
      }
    }
  }

  return null;
}

export function buildServicesPayload(selections) {
  if (!selections || typeof selections !== 'object') return [];

  return RESTAURANT_SERVICE_TYPES.filter((type) => selections[type]?.selected).map((type) =>
    buildServicePayload(type, selections[type] || {})
  );
}

export function validateServicesSelection(selections) {
  const selected = buildServicesPayload(selections);
  for (const row of selected) {
    const hall = row.partyHall || {};
    const error = validateServicePayload(row.serviceType, {
      ...row,
      ...hall,
      amenities: Array.isArray(hall.amenities) ? hall.amenities.join(', ') : hall.amenities,
      eventTypes: Array.isArray(hall.eventTypes) ? hall.eventTypes.join(', ') : hall.eventTypes,
    });
    if (error) return error;
  }
  return null;
}

export function emptyServiceSelections() {
  return RESTAURANT_SERVICE_TYPES.reduce((acc, type) => {
    acc[type] = {
      selected: false,
      status: 'ACTIVE',
      minGuestCount: '',
      maxGuestCount: '',
      noOfTables: '',
      ...emptyPartyHallFields(),
    };
    return acc;
  }, {});
}

export function emptyServiceForm() {
  return {
    serviceType: '',
    status: 'ACTIVE',
    minGuestCount: '',
    maxGuestCount: '',
    noOfTables: '',
    ...emptyPartyHallFields(),
  };
}

export function formatGuestCountRange(row) {
  const min = row?.minGuestCount ?? row?.guestCount;
  const max = row?.maxGuestCount;
  if (min == null && max == null) return '—';
  if (max == null) return String(min);
  if (min == null) return `Up to ${max}`;
  return `${min} – ${max}`;
}

export function formatNoOfTables(row) {
  const value = row?.noOfTables ?? row?.no_of_tables ?? row?.No_Of_Tables;
  return value == null || value === '' ? '—' : String(value);
}

export function formatHallType(row) {
  const hall = row?.partyHall || row;
  const code = String(hall?.hallType || hall?.Hall_Type || '').trim().toUpperCase();
  if (!code) return '—';
  return PARTY_HALL_TYPE_LABELS[code] || code;
}
